/**
 * Truy cập dữ liệu cấu hình bảo mật theo tầng (ADR-0059): danh mục tham số,
 * chính sách ba tầng, chính sách mật khẩu, lịch sử, báo cáo IP, blocklist của nền
 * tảng và của tenant (ADR-0090) và thông báo — tenant-service qua Gateway (`openapi/platform-api.yaml`,
 * tag `security`, `notifications`). Riêng tài khoản đang bị khoá tạm (mục 5.3)
 * do user-service phục vụ dưới cùng tiền tố `/tenants/{id}`; cảnh báo audit đã
 * kích hoạt (mục 4.1) do audit-service phục vụ.
 *
 * Mỗi hàm chọn API thật hay dữ liệu mẫu bằng `dungApiThat()`, như mọi
 * repository khác. Phản hồi thật đi qua `nghiep-vu/anh-xa-api.ts` để về đúng
 * hình dạng màn hình.
 *
 * Mock giải ba tầng bằng **chính** `giaiCaiDat()` và kiểm bằng **chính**
 * `kiemGiaTri()` của `modules/bao-mat/nghiep-vu`, nên form chạy đủ nhánh lỗi
 * (412, 400 theo trường) giống API thật.
 *
 * Diễn thêm hai trạng thái ngoài `?mock=` chung:
 * - `?mock=conflict`: mọi lần ghi trả `VERSION_MISMATCH` (412) như thể có người
 *   vừa sửa trước — để soát dải báo xung đột.
 */
import { dungApiThat, goiApi } from '../api/http';
import { CO_TRANG_TOI_DA, docHetTrang } from '../api/phan-trang';
import type { TrangCon } from '../api/phan-trang';
import type { ApiError } from '../errors';
import {
  MOCK_CAI_DAT_GOI,
  MOCK_CANH_BAO,
  MOCK_CANH_BAO_MAT,
  MOCK_CAU_LOI,
  MOCK_CHI_TIET_LOI,
  MOCK_CAI_DAT_NEN_TANG,
  MOCK_CAI_DAT_TENANT,
  MOCK_CHAN_NEN_TANG,
  MOCK_CHAN_TENANT,
  MOCK_DANH_MUC,
  MOCK_GOI_CUA_TENANT,
  MOCK_IP_MONITOR,
  MOCK_IP_NGUOI_GOI,
  MOCK_KHOA_TAM,
  MOCK_KHOA_TAM_MAT_TAI_KHOAN,
  MOCK_LICH_SU_TENANT,
  MOCK_THONG_BAO,
  MOCK_TRANG_THAI_DONG_BO,
} from '../../mock/bao-mat';
import type { PhienBanMau, TaiLieuCaiDat } from '../../mock/bao-mat';
import { mayChuTenant } from '../../mock/tenant';
import { mockRequest, mockState } from '../../mock/transport';
import { phamViCuaVaiTro } from '../pham-vi-cong';
import { useAuthStore } from '../../stores/auth';
import { bienHieuLuc, giaiCaiDat, kiemGiaTri } from '../../modules/bao-mat/nghiep-vu/cai-dat';
import {
  chinhSachTuApi,
  ifMatchTuPhienBan,
  matKhauTuApi,
} from '../../modules/bao-mat/nghiep-vu/anh-xa-api';
import type { PasswordPolicyApi, SecurityPolicyViewApi } from '../../modules/bao-mat/nghiep-vu/anh-xa-api';
import { chuanHoaCidr, ipThuocCidr } from '../../modules/bao-mat/nghiep-vu/cidr';
import type {
  BaoCaoIpMonitor,
  BoLocCanhBao,
  CanhBaoAudit,
  DanhMucCaiDat,
  DongChanIp,
  DongCidr,
  GhiCaiDatVao,
  KetQuaGuiThu,
  LoiTruong,
  MaGoi,
  PhamViCaiDat,
  PhamViChan,
  SecurityPolicyView,
  ThemChanVao,
  TaiKhoanKhoaTam,
  TrangCanhBao,
  TrangKhoaTam,
  TrangPhienBan,
  TrangThongBao,
  TuyenThongBao,
  VaiTroSua,
} from '../../modules/bao-mat/types';

/** Sáu khoá của `GET/PUT /api/v1/security/password-policy`. */
export const KHOA_MAT_KHAU = [
  'auth.password_min_length',
  'auth.password_max_length',
  'auth.password_history',
  'auth.bruteforce_max_failures',
  'auth.bruteforce_lock_minutes',
  'auth.password_blacklist_enabled',
] as const;

// ── Trạng thái trong phiên: thao tác ghi thấy được ngay, như API thật. ──
const sao = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
let nenTang: TaiLieuCaiDat = sao(MOCK_CAI_DAT_NEN_TANG);
const theoGoi: Record<MaGoi, TaiLieuCaiDat> = sao(MOCK_CAI_DAT_GOI);
const theoTenant: Record<string, TaiLieuCaiDat> = sao(MOCK_CAI_DAT_TENANT);
const lichSu: Record<string, PhienBanMau[]> = sao(MOCK_LICH_SU_TENANT);
let chanNenTang: DongChanIp[] = sao(MOCK_CHAN_NEN_TANG);
const chanTenant: Record<string, DongChanIp[]> = sao(MOCK_CHAN_TENANT);
let dongBo = MOCK_TRANG_THAI_DONG_BO;
const khoaTam: Record<string, TaiKhoanKhoaTam[][]> = sao(MOCK_KHOA_TAM);
const canhBao: Record<string, CanhBaoAudit[]> = sao(MOCK_CANH_BAO);
const daDungKhoaLap = new Map<string, DongChanIp>();

/** Vai trò lớp 1 của người đang đăng nhập — API thật đọc từ token. */
function vaiTroHienTai(): VaiTroSua {
  const user = useAuthStore.getState().user;
  return phamViCuaVaiTro(user?.roles) === 'nen-tang' ? 'PLATFORM_ADMIN' : 'TENANT_ADMIN';
}

const gia = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  ...(details ? { details } : {}),
});

const tuChoi = <T>(loi: ApiError): Promise<T> =>
  mockRequest<null>(null).then(() => Promise.reject(loi));

function taiLieuTenant(id: string): TaiLieuCaiDat {
  theoTenant[id] ??= {
    // Tenant tạo với "dùng email cá nhân" (ADR-0099) có sẵn giá trị tầng tenant, như tenant-service ghi lúc tạo
    settings: mayChuTenant.chiEmailDomainLucTao(id) === false ? { 'auth.chi_email_domain_xac_minh': false } : {},
    version: 0,
    updated_at: new Date().toISOString(),
    updated_by: null,
  };
  return theoTenant[id];
}

function dungView(phamVi: PhamViCaiDat, khoa: string, maGoi: MaGoi | null): SecurityPolicyView {
  const tl =
    phamVi === 'PLATFORM' ? nenTang : phamVi === 'PLAN' ? theoGoi[khoa as MaGoi] : taiLieuTenant(khoa);
  return {
    scope: phamVi,
    scope_key: khoa,
    plan_code: maGoi,
    version: tl.version,
    settings: giaiCaiDat({
      danhMuc: MOCK_DANH_MUC,
      nenTang: nenTang.settings,
      goi: maGoi ? theoGoi[maGoi].settings : {},
      tenant: phamVi === 'TENANT' ? tl.settings : {},
      phamVi,
      maGoi,
      vaiTro: vaiTroHienTai(),
    }),
    updated_at: tl.updated_at,
    updated_by: tl.updated_by,
    caller_ip: MOCK_IP_NGUOI_GOI,
    ...(phamVi === 'TENANT' ? { verified_domains: mayChuTenant.domainDaXacMinh(khoa) } : {}),
  };
}

/**
 * Kiểm một lần ghi như `SettingsValidator` + bước 2–3 của ADR-0059 mục 1.5:
 * phiên bản, quyền theo từng khoá, biên, rồi "không tự khoá mình".
 */
function kiemGhi(
  view: SecurityPolicyView,
  ifMatch: number,
  vao: GhiCaiDatVao,
): ApiError | null {
  if (
    new URLSearchParams(window.location.search).get('mock') === 'conflict' ||
    ifMatch !== view.version
  ) {
    return gia('VERSION_MISMATCH', 412, MOCK_CAU_LOI.phienBanCu(ifMatch));
  }

  const loi: LoiTruong[] = [];
  const chiSiet = view.scope === 'TENANT' && vaiTroHienTai() === 'TENANT_ADMIN';
  for (const [key, giaTri] of Object.entries(vao.settings)) {
    const m = view.settings.find((x) => x.key === key);
    const def = MOCK_DANH_MUC.find((d) => d.key === key);
    const contro = `#/settings/${key}`;
    if (!m || !def || !m.editable) {
      loi.push({ pointer: contro, detail: MOCK_CHI_TIET_LOI.khongDuocSua! });
      continue;
    }
    if (giaTri === null) continue;
    // Biên của dòng (đã gồm mức sàn kế thừa của ADR-0078), như backend
    const kq = kiemGiaTri(def, m.bounds ?? bienHieuLuc(def, view.plan_code), giaTri, {
      bayGio: Date.now(),
      keThua: m.inherited_value,
      chiSiet,
    });
    if (!kq) continue;
    // Quy tắc cảnh báo: backend trỏ tới đúng trường của đúng quy tắc.
    const p = kq.thamSo;
    const conTro =
      kq.ma === 'quy_tac_sai' && p ? `${contro}/${Number(p.dong) - 1}/${String(p.truong)}` : contro;
    loi.push({ pointer: conTro, detail: MOCK_CHI_TIET_LOI[kq.ma] ?? MOCK_CAU_LOI.cauHinhSai });
  }

  // Dải tin cậy không chặn ai (ADR-0098) nên không còn kiểm tự khoá mình khi đổi dải.

  return loi.length > 0
    ? gia('INVALID_ARGUMENT', 400, MOCK_CAU_LOI.cauHinhSai, loi)
    : null;
}

/** Chuẩn hoá CIDR trong giá trị trước khi lưu, như backend. */
function chuanHoaGiaTri(giaTri: unknown): unknown {
  if (!Array.isArray(giaTri) || giaTri.length === 0 || typeof giaTri[0] !== 'object') return giaTri;
  return (giaTri as DongCidr[]).map((d) => {
    const kq = chuanHoaCidr(d.cidr);
    return { ...d, cidr: kq.ok ? kq.cidr : d.cidr };
  });
}

function apDung(tl: TaiLieuCaiDat, vao: GhiCaiDatVao): Record<string, unknown> {
  const moi = { ...tl.settings };
  for (const [k, v] of Object.entries(vao.settings)) {
    if (v === null) delete moi[k];
    else moi[k] = chuanHoaGiaTri(v);
  }
  tl.settings = moi;
  tl.version += 1;
  tl.updated_at = new Date().toISOString();
  tl.updated_by = useAuthStore.getState().user?.email ?? null;
  return moi;
}

function ghiTang(
  phamVi: PhamViCaiDat,
  khoa: string,
  maGoi: MaGoi | null,
  ifMatch: number,
  vao: GhiCaiDatVao,
): Promise<SecurityPolicyView> {
  const truoc = dungView(phamVi, khoa, maGoi);
  const loi = kiemGhi(truoc, ifMatch, vao);
  if (loi) return tuChoi(loi);

  const tl =
    phamVi === 'PLATFORM' ? nenTang : phamVi === 'PLAN' ? theoGoi[khoa as MaGoi] : taiLieuTenant(khoa);
  const settingsCu = { ...tl.settings };
  const moi = apDung(tl, vao);

  if (phamVi === 'TENANT') {
    (lichSu[khoa] ??= []).unshift({
      version: tl.version,
      changed_by: tl.updated_by,
      changed_at: tl.updated_at,
      reason: vao.reason?.trim() || null,
      changes: Object.keys(vao.settings).map((key) => ({
        key,
        before: settingsCu[key] ?? null,
        after: moi[key] ?? null,
      })),
      settings: sao(moi),
    });
  }
  return mockRequest(dungView(phamVi, khoa, maGoi));
}

/** Phân trang con trỏ chung cho mock: con trỏ là vị trí bắt đầu. */
function catTrang<T>(ds: T[], cursor: string | null | undefined, limit: number) {
  const tu = cursor ? Number(cursor) : 0;
  const items = ds.slice(tu, tu + limit);
  return { items, next_cursor: tu + limit < ds.length ? String(tu + limit) : null };
}

const baoMatMock = {
  /** `GET /api/v1/security/settings-schema` — đổi khi deploy, không đổi trong phiên. */
  danhMuc: (): Promise<DanhMucCaiDat> => mockRequest({ settings: MOCK_DANH_MUC }),

  // ── Tầng tenant ──
  tenant: (id: string): Promise<SecurityPolicyView> =>
    mockRequest(dungView('TENANT', id, MOCK_GOI_CUA_TENANT[id] ?? 'STANDARD')),

  ghiTenant: (id: string, ifMatch: number, vao: GhiCaiDatVao): Promise<SecurityPolicyView> =>
    ghiTang('TENANT', id, MOCK_GOI_CUA_TENANT[id] ?? 'STANDARD', ifMatch, vao),

  lichSu: (id: string, cursor?: string | null): Promise<TrangPhienBan> => {
    const ds = (lichSu[id] ?? []).map(({ settings: _bo, ...p }) => p);
    return mockRequest(catTrang(ds, cursor, 20), { items: [], next_cursor: null });
  },

  /** Khôi phục = một thay đổi mới mang nội dung của phiên bản đó (ADR-0059 mục 1.5). */
  khoiPhuc: (id: string, version: number, ifMatch: number): Promise<SecurityPolicyView> => {
    const pb = (lichSu[id] ?? []).find((p) => p.version === version);
    if (!pb) return tuChoi(gia('NOT_FOUND', 404, MOCK_CAU_LOI.khongCoPhienBan(version)));
    const hienTai = taiLieuTenant(id).settings;
    const settings: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(hienTai), ...Object.keys(pb.settings)])) {
      settings[k] = k in pb.settings ? pb.settings[k] : null;
    }
    return ghiTang('TENANT', id, MOCK_GOI_CUA_TENANT[id] ?? 'STANDARD', ifMatch, {
      settings,
      reason: MOCK_CAU_LOI.khoiPhuc(version),
    });
  },

  ipMonitor: (id: string, hours = 72): Promise<BaoCaoIpMonitor> => {
    if (mockState() === 'filtered-empty') {
      return mockRequest({ from: '', to: '', items: [] });
    }
    const to = new Date();
    return mockRequest(
      {
        from: new Date(to.getTime() - hours * 3_600_000).toISOString(),
        to: to.toISOString(),
        items: MOCK_IP_MONITOR[id] ?? [],
      },
      { from: '', to: '', items: [] },
    );
  },

  /**
   * Một khúc tra mỗi lần gọi, như user-service: con trỏ là số thứ tự khúc, khúc
   * rỗng vẫn trả `next_cursor`. `limit` không cắt vì mỗi khúc mẫu ít người.
   */
  khoaTam: (id: string, cursor?: string | null): Promise<TrangKhoaTam> => {
    if (mockState() === 'filtered-empty') return mockRequest({ items: [], next_cursor: null });
    const khuc = khoaTam[id] ?? [];
    const so = cursor ? Number(cursor) : 0;
    return mockRequest(
      { items: khuc[so] ?? [], next_cursor: so + 1 < khuc.length ? String(so + 1) : null },
      { items: [], next_cursor: null },
    );
  },

  /** Luỹ đẳng như API thật: người không bị khoá tạm cũng thành công. */
  moKhoaTam: (id: string, userId: string): Promise<void> => {
    if (MOCK_KHOA_TAM_MAT_TAI_KHOAN.has(userId)) {
      return tuChoi(gia('USER_NOT_FOUND', 404, MOCK_CAU_LOI.khongCoNguoiKhoaTam));
    }
    khoaTam[id] = (khoaTam[id] ?? []).map((k) => k.filter((d) => d.user_id !== userId));
    return mockRequest(undefined);
  },

  /**
   * Cảnh báo đã kích hoạt, lọc ở "server". `?mock=filtered-empty` chỉ rỗng khi
   * đang lọc — chọn một mức hoặc trạng thái ở bộ lọc để soát.
   */
  canhBao: (id: string, boLoc: BoLocCanhBao, cursor?: string | null): Promise<TrangCanhBao> => {
    const dangLoc = boLoc.severity !== null || boLoc.status !== null;
    if (mockState() === 'filtered-empty' && dangLoc) return mockRequest({ items: [], next_cursor: null });
    const ds = (canhBao[id] ?? []).filter(
      (c) =>
        (boLoc.severity === null || c.severity === boLoc.severity) &&
        (boLoc.status === null || c.status === boLoc.status),
    );
    return mockRequest(catTrang(ds, cursor, 20), { items: [], next_cursor: null });
  },

  /** Luỹ đẳng như audit-service: đã ACK thì trả nguyên bản ghi. */
  xacNhanCanhBao: (id: string, alertId: string): Promise<CanhBaoAudit> => {
    const c = (canhBao[id] ?? []).find((x) => x.id === alertId);
    if (!c || MOCK_CANH_BAO_MAT.has(alertId)) {
      return tuChoi(gia('NOT_FOUND', 404, MOCK_CAU_LOI.khongCoCanhBao));
    }
    if (c.status === 'OPEN') {
      c.status = 'ACKNOWLEDGED';
      c.acknowledged_at = new Date().toISOString();
      c.acknowledged_by = useAuthStore.getState().user?.email ?? null;
    }
    return mockRequest({ ...c });
  },

  // ── Tầng nền tảng và tầng gói ──
  nenTang: (): Promise<SecurityPolicyView> => mockRequest(dungView('PLATFORM', '1', null)),

  ghiNenTang: (ifMatch: number, vao: GhiCaiDatVao): Promise<SecurityPolicyView> =>
    ghiTang('PLATFORM', '1', null, ifMatch, vao),

  goi: (ma: MaGoi): Promise<SecurityPolicyView> => mockRequest(dungView('PLAN', ma, ma)),

  ghiGoi: (ma: MaGoi, ifMatch: number, vao: GhiCaiDatVao): Promise<SecurityPolicyView> =>
    ghiTang('PLAN', ma, ma, ifMatch, vao),

  /** Sáu khoá mật khẩu ở tầng nền tảng, kèm trạng thái đồng bộ Keycloak. */
  matKhau: (): Promise<SecurityPolicyView> => {
    const v = dungView('PLATFORM', '1', null);
    return mockRequest({
      ...v,
      settings: v.settings.filter((m) => (KHOA_MAT_KHAU as readonly string[]).includes(m.key)),
      sync_status: dongBo,
    });
  },

  ghiMatKhau: async (ifMatch: number, vao: GhiCaiDatVao): Promise<SecurityPolicyView> => {
    const v = await ghiTang('PLATFORM', '1', null, ifMatch, vao);
    // Client `platform-realm-config` tắt mặc định → lưu được nhưng chưa đồng bộ.
    dongBo = dongBo === 'DISABLED' ? 'DISABLED' : 'PENDING';
    return {
      ...v,
      settings: v.settings.filter((m) => (KHOA_MAT_KHAU as readonly string[]).includes(m.key)),
      sync_status: dongBo,
    };
  },

  // ── Blocklist của nền tảng và của tenant (ADR-0090 mục 3) ──
  /** Nền tảng trả cả dòng đã hết hạn (job dọn sau); tenant chỉ trả dòng còn hiệu lực. */
  danhSachChan: (phamVi: PhamViChan): Promise<{ items: DongChanIp[] }> => {
    if (mockState() === 'filtered-empty') return mockRequest({ items: [] });
    const ds =
      phamVi.loai === 'tenant'
        ? (chanTenant[phamVi.tenantId] ?? []).filter((d) => !d.expires_at || Date.parse(d.expires_at) > Date.now())
        : chanNenTang;
    return mockRequest({ items: ds }, { items: [] });
  },

  themChan: (phamVi: PhamViChan, vao: ThemChanVao, khoaLap: string): Promise<DongChanIp> => {
    const cu = daDungKhoaLap.get(khoaLap);
    if (cu) return mockRequest(cu);
    const kq = chuanHoaCidr(vao.cidr);
    if (!kq.ok || kq.tienTo === 0) {
      return tuChoi(
        gia('INVALID_ARGUMENT', 400, MOCK_CAU_LOI.cidrSai, [
          { pointer: '#/cidr', detail: MOCK_CHI_TIET_LOI.cidr_sai },
        ]),
      );
    }
    const ds = phamVi.loai === 'tenant' ? (chanTenant[phamVi.tenantId] ??= []) : chanNenTang;
    // Không tự khoá mình (ADR-0098): Tenant Admin không chặn được dải chứa IP của chính mình ở blocklist tenant;
    // blocklist nền tảng áp cả cổng nền tảng nên kiểm cả Quản trị nền tảng
    const kiemTuKhoa = phamVi.loai === 'nen-tang' || vaiTroHienTai() === 'TENANT_ADMIN';
    if (kiemTuKhoa && ipThuocCidr(MOCK_IP_NGUOI_GOI, kq.cidr)) {
      return tuChoi(
        gia('INVALID_ARGUMENT', 400, MOCK_CAU_LOI.cauHinhSai, [
          { pointer: '#/cidr', detail: MOCK_CAU_LOI.chuaIpCuaBan(kq.cidr, MOCK_IP_NGUOI_GOI) },
        ]),
      );
    }
    if (ds.some((d) => d.cidr === kq.cidr)) {
      return tuChoi(
        gia('INVALID_ARGUMENT', 400, MOCK_CAU_LOI.cauHinhSai, [
          { pointer: '#/cidr', detail: MOCK_CAU_LOI.daCoTrongBlocklist(kq.cidr) },
        ]),
      );
    }
    const dong: DongChanIp = {
      id: `bl-${Date.now()}`,
      cidr: kq.cidr,
      reason: vao.reason.trim(),
      source: 'MANUAL',
      expires_at: vao.duration_minutes ? new Date(Date.now() + vao.duration_minutes * 60_000).toISOString() : null,
      created_by: useAuthStore.getState().user?.id ?? null,
      created_at: new Date().toISOString(),
    };
    ds.unshift(dong);
    if (phamVi.loai === 'nen-tang') chanNenTang = [...ds];
    daDungKhoaLap.set(khoaLap, dong);
    return mockRequest(dong);
  },

  xoaChan: (phamVi: PhamViChan, id: string): Promise<{ id: string }> => {
    if (phamVi.loai === 'tenant') {
      chanTenant[phamVi.tenantId] = (chanTenant[phamVi.tenantId] ?? []).filter((d) => d.id !== id);
    } else {
      chanNenTang = chanNenTang.filter((d) => d.id !== id);
    }
    return mockRequest({ id });
  },

  // ── Thông báo ──
  guiThu: (notify_key: string, tenant_id?: string): Promise<KetQuaGuiThu> => {
    const v = dungView(tenant_id ? 'TENANT' : 'PLATFORM', tenant_id ?? '1', null);
    const r = v.settings.find((m) => m.key === notify_key)?.value as TuyenThongBao | undefined;
    const recipients = r?.recipients ?? [];
    return mockRequest({ status: recipients.length > 0 ? 'SENT' : 'NO_RECIPIENT', recipients });
  },

  thongBao: (cursor?: string | null, tenant_id?: string): Promise<TrangThongBao> => {
    if (mockState() === 'filtered-empty') return mockRequest({ items: [], next_cursor: null });
    const ds = tenant_id ? MOCK_THONG_BAO.filter((n) => n.tenant_id === tenant_id) : MOCK_THONG_BAO;
    return mockRequest(catTrang(ds, cursor, 20), { items: [], next_cursor: null });
  },
};

// ── API thật ──

/** Số người tối đa mỗi trang; user-service vẫn chỉ tra một khúc hồ sơ mỗi lần. */
const MOT_TRANG_KHOA_TAM = 50;

const DM = '/api/v1/security';
const tenantGoc = (id: string) => `/api/v1/tenants/${encodeURIComponent(id)}/security`;
const duongDanChan = (phamVi: PhamViChan) =>
  phamVi.loai === 'tenant' ? `${tenantGoc(phamVi.tenantId)}/ip-blocklist` : `${DM}/ip-blocklist`;

/** Đọc một chính sách (`SecurityPolicyView`) rồi chuẩn hoá. */
const docChinhSach = (duongDan: string): Promise<SecurityPolicyView> =>
  goiApi<SecurityPolicyViewApi>(duongDan).then(chinhSachTuApi);

/**
 * Ghi một chính sách với `If-Match`. Lệch phiên bản thì backend trả `412
 * VERSION_MISMATCH`; lỗi theo trường (`400 INVALID_ARGUMENT`) mang
 * `errors[].pointer` dạng `#/settings/<khoá>` — form đọc qua `khoaCuaPointer`.
 */
const ghiChinhSach = (
  duongDan: string,
  ifMatch: number,
  vao: GhiCaiDatVao,
): Promise<SecurityPolicyView> =>
  goiApi<SecurityPolicyViewApi>(duongDan, {
    method: 'PUT',
    ifMatch: ifMatchTuPhienBan(ifMatch),
    // `reason` bắt buộc trong `SecurityPolicyUpdate`; form để trống thì gửi chuỗi rỗng.
    body: {
      settings: vao.settings,
      reason: vao.reason?.trim() ?? '',
      ...(vao.mfa_apply ? { mfa_apply: vao.mfa_apply } : {}),
    },
  }).then(chinhSachTuApi);

/** Sáu khoá mật khẩu: `PasswordPolicy` → cùng hình dạng với ba tầng. */
const docMatKhau = (): Promise<SecurityPolicyView> =>
  goiApi<PasswordPolicyApi>(`${DM}/password-policy`).then(matKhauTuApi);

const baoMatThat: typeof baoMatMock = {
  danhMuc: () => goiApi<DanhMucCaiDat>(`${DM}/settings-schema`),

  tenant: (id) => docChinhSach(tenantGoc(id)),
  ghiTenant: (id, ifMatch, vao) => ghiChinhSach(tenantGoc(id), ifMatch, vao),

  lichSu: (id, cursor) =>
    goiApi<TrangPhienBan>(`${tenantGoc(id)}/revisions`, {
      query: { limit: 20, cursor: cursor ?? undefined },
    }),

  /** Khôi phục không có thân; backend đi lại đủ các bước kiểm của `PUT`. */
  khoiPhuc: (id, version, ifMatch) =>
    goiApi<SecurityPolicyViewApi>(`${tenantGoc(id)}/revisions/${version}/restore`, {
      method: 'POST',
      ifMatch: ifMatchTuPhienBan(ifMatch),
    }).then(chinhSachTuApi),

  ipMonitor: (id, hours = 72) =>
    goiApi<BaoCaoIpMonitor>(`${tenantGoc(id)}/ip-monitor`, {
      query: { hours, limit: CO_TRANG_TOI_DA },
    }),

  khoaTam: (id, cursor) =>
    goiApi<TrangKhoaTam>(`/api/v1/tenants/${encodeURIComponent(id)}/locked-users`, {
      query: { limit: MOT_TRANG_KHOA_TAM, cursor: cursor ?? undefined },
    }),

  /** `204`, không thân, không `Idempotency-Key` (không tạo tài nguyên). */
  moKhoaTam: async (id, userId) => {
    await goiApi<void>(
      `/api/v1/tenants/${encodeURIComponent(id)}/locked-users/${encodeURIComponent(userId)}/unlock`,
      { method: 'POST' },
    );
  },

  /** `limit` để server tự chọn (mặc định 20 của `Limit` chuẩn). */
  canhBao: (id, boLoc, cursor) =>
    goiApi<TrangCanhBao>(`/api/v1/tenants/${encodeURIComponent(id)}/audit/alerts`, {
      query: {
        severity: boLoc.severity ?? undefined,
        status: boLoc.status ?? undefined,
        cursor: cursor ?? undefined,
      },
    }),

  /** Không thân; trả `AuditAlert` sau khi ACK (luỹ đẳng). */
  xacNhanCanhBao: (id, alertId) =>
    goiApi<CanhBaoAudit>(
      `/api/v1/tenants/${encodeURIComponent(id)}/audit/alerts/${encodeURIComponent(alertId)}/ack`,
      { method: 'POST' },
    ),

  nenTang: () => docChinhSach(`${DM}/platform`),
  ghiNenTang: (ifMatch, vao) => ghiChinhSach(`${DM}/platform`, ifMatch, vao),

  goi: (ma) => docChinhSach(`${DM}/plans/${encodeURIComponent(ma)}`),
  ghiGoi: (ma, ifMatch, vao) => ghiChinhSach(`${DM}/plans/${encodeURIComponent(ma)}`, ifMatch, vao),

  matKhau: docMatKhau,

  /** `PasswordPolicyUpdate` chỉ có `settings`, `reason` — không có `mfa_apply`. */
  ghiMatKhau: (ifMatch, vao) =>
    goiApi<PasswordPolicyApi>(`${DM}/password-policy`, {
      method: 'PUT',
      ifMatch: ifMatchTuPhienBan(ifMatch),
      body: { settings: vao.settings, reason: vao.reason?.trim() ?? '' },
    }).then(matKhauTuApi),

  /** Đọc hết các trang: mỗi danh sách tối đa 200 dòng còn hiệu lực. */
  danhSachChan: async (phamVi) => ({
    items: await docHetTrang((cursor) =>
      goiApi<TrangCon<DongChanIp>>(duongDanChan(phamVi), {
        query: { limit: CO_TRANG_TOI_DA, cursor },
      }),
    ),
  }),

  themChan: (phamVi, vao, khoaLap) =>
    goiApi<DongChanIp>(duongDanChan(phamVi), {
      method: 'POST',
      idempotencyKey: khoaLap,
      body: { cidr: vao.cidr, reason: vao.reason.trim(), duration_minutes: vao.duration_minutes },
    }),

  xoaChan: async (phamVi, id) => {
    await goiApi<void>(`${duongDanChan(phamVi)}/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return { id };
  },

  guiThu: (notify_key, tenant_id) =>
    goiApi<KetQuaGuiThu>(`${DM}/notifications/test`, {
      method: 'POST',
      body: { notify_key, tenant_id: tenant_id ?? null },
    }),

  thongBao: (cursor, tenant_id) =>
    goiApi<TrangThongBao>('/api/v1/notifications', {
      query: { tenant_id, limit: 20, cursor: cursor ?? undefined },
    }),
};

/**
 * Chọn nguồn **lúc gọi** chứ không lúc nạp module: `?mock=` đổi được giữa hai
 * lần điều hướng mà không tải lại trang (xem `dungApiThat`).
 */
const chon = () => (dungApiThat() ? baoMatThat : baoMatMock);

export const baoMatRepository: typeof baoMatMock = {
  danhMuc: () => chon().danhMuc(),
  tenant: (id) => chon().tenant(id),
  ghiTenant: (id, ifMatch, vao) => chon().ghiTenant(id, ifMatch, vao),
  lichSu: (id, cursor) => chon().lichSu(id, cursor),
  khoiPhuc: (id, version, ifMatch) => chon().khoiPhuc(id, version, ifMatch),
  ipMonitor: (id, hours) => chon().ipMonitor(id, hours),
  khoaTam: (id, cursor) => chon().khoaTam(id, cursor),
  moKhoaTam: (id, userId) => chon().moKhoaTam(id, userId),
  canhBao: (id, boLoc, cursor) => chon().canhBao(id, boLoc, cursor),
  xacNhanCanhBao: (id, alertId) => chon().xacNhanCanhBao(id, alertId),
  nenTang: () => chon().nenTang(),
  ghiNenTang: (ifMatch, vao) => chon().ghiNenTang(ifMatch, vao),
  goi: (ma) => chon().goi(ma),
  ghiGoi: (ma, ifMatch, vao) => chon().ghiGoi(ma, ifMatch, vao),
  matKhau: () => chon().matKhau(),
  ghiMatKhau: (ifMatch, vao) => chon().ghiMatKhau(ifMatch, vao),
  danhSachChan: (phamVi) => chon().danhSachChan(phamVi),
  themChan: (phamVi, vao, khoaLap) => chon().themChan(phamVi, vao, khoaLap),
  xoaChan: (phamVi, id) => chon().xoaChan(phamVi, id),
  guiThu: (notifyKey, tenantId) => chon().guiThu(notifyKey, tenantId),
  thongBao: (cursor, tenantId) => chon().thongBao(cursor, tenantId),
};
